import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { AdsContext } from '@components/Ads/context';
import cn from 'classnames';
import { cubicBezier, motion } from 'framer-motion';

import { useShopBuyMutation } from '@/api/queries/shop';
import { useCheckVIPstatus } from '@/common/hooks/checkVIPstatus';
import { useCountDownTimer } from '@/common/hooks/count-down-timer';
import { SendAnalitics } from '@/common/lib/playdeck';
import {
  type BoxReward,
  type MarketBoxes,
} from '@/common/types/shop/marketBoxes';
import { Confetti } from '@/components/Animations/Confetti/Confetti';
import { CoinBalanceTopUp } from '@/components/CoinBalanceTopUp/CoinBalanceTopUp';
import { shopBoxesImages } from '@/components/helpers/imagesImports';
import ChengeButton from '@/components/OffersMarket/BoxMarket/BoxCard/checkButtons';
import RewardList from '@/components/OffersMarket/BoxMarket/BoxCard/RewardList/RewardList';
import { H1, StringMain } from '@/components/Typography/Typography';
import { Close } from '@/components/ui/Close/Close';
import RadialCircleGradient, {
  type TypeGradient,
} from '@/components/ui/Gradient/RadialCircleGradient';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import { addToBalance, addToStorage } from '@/redux/reducers/userReducer';
import { useAppSelector } from '@/redux/store';

import { ModalContext } from '../Modal/config';

import css from './style.module.scss';

import shinePng from '/img/circle-light-effect.png';
import shineWebp from '/img/circle-light-effect.webp';

interface props {
  name: MarketBoxes;
  title: string;
  gradient: TypeGradient;
  reward: BoxReward;
  tgem: number;
  boxSeconds: number;
  cardType: 'small' | 'medium' | 'big';
}

const BuyMarketBox: React.FC<props> = ({
  name,
  title,
  gradient,
  reward,
  tgem,
  boxSeconds,
  cardType,
}) => {
  const [timerDuration, setTimerDuration] = useState<number>(boxSeconds || 0);
  const { secondModal, closeModal } = useContext(ModalContext);
  const tgemBalance = useAppSelector((state) => state.user.user?.balance.tgem);
  const [buyBox, { isLoading }] = useShopBuyMutation();
  const timer = useCountDownTimer(timerDuration);
  const { t } = useTranslation();
  const { showAd, adsIsLoading } = useContext(AdsContext);
  const statusVIP = useCheckVIPstatus();
  const dispatch = useDispatch();
  const img = shopBoxesImages[name];
  const [smallboxReward, setSmallBoxReward] = useState<BoxReward | null>(null);
  const [isBuy, setIsBuy] = useState(false);

  const checkReward: BoxReward | null =
    cardType === 'small' ? smallboxReward : reward;

  const flattenReward = (
    reward: BoxReward,
  ): { item: string; value: number }[] => {
    const result: { item: string; value: number }[] = [];

    if (reward.resources) {
      reward.resources.forEach((resource) =>
        Object.entries(resource).forEach(([key, value]) => {
          result.push({ item: key, value });
        }),
      );
    }

    if (reward.materials) {
      reward.materials.forEach((material) =>
        Object.entries(material).forEach(([key, value]) => {
          result.push({ item: key, value });
        }),
      );
    }

    return result;
  };

  const onReward = (): void => {
    if ((tgemBalance || 0) < tgem) {
      secondModal.openModal({
        renderKind: 'coinDeposit',
        props: { source: 'market_box_no_balance' },
      });
    } else {
      buyBox({ name })
        .unwrap()
        .then((data) => {
          setSmallBoxReward(data.reward);
          setTimerDuration(data.nextUseInSeconds || 0);
          dispatch(
            addToBalance({
              cash: data.reward.cash || 0,
              tgem: (tgem || 0) * -1,
            }),
          );
          SendAnalitics.coinSpend({
            amount: tgem || 0,
            type: 'market_box',
            item_name: name,
          });
          const flattenedReward = flattenReward(data.reward);
          dispatch(addToStorage(flattenedReward));

          if (name === 'free') {
            localStorage.setItem(
              'claim-free-box-date',
              new Date().toISOString(),
            );
          }
          setIsBuy(true);
        });
    }
  };

  const onBuyBox = (): void => {
    if (name === 'ads') {
      if (statusVIP) {
        onReward();
      } else {
        showAd({
          placements: 'marketBox',
          onReward,
          onError: () => {
            showToast(t('Ads launch error'));
          },
        });
      }
    } else {
      onReward();
    }
  };

  return (
    <div className={cn(css.buyBoxModal, 'relative')}>
      {isBuy && (
        <Confetti
          className="-z-[1] w-full h-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          loop={false}
          autoPlay={true}
          speed={2}
          width="100%"
          height="100%"
        />
      )}
      <CoinBalanceTopUp
        className="!h-12 !pl-2 !pr-3 py-[0.625rem]"
        contenctClassName="!items-start"
        compare={Infinity}
        iconSize="1.75rem"
      />
      <div className={css.title}>
        <H1>{t(title)}</H1>
        <p>
          {t('You are buying a useful set for everyday tasks, use it quickly!')}
        </p>
      </div>
      <div className="flex items-center w-full px-[10%]">
        {checkReward && (
          <motion.div
            className={cn('flex items-center w-full flex-col gap-[8px]')}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              ease: cubicBezier(0.68, -0.55, 0.27, 1.55),
              duration: 0.3,
            }}
          >
            <RewardList
              variant="big"
              packName={name}
              reward={checkReward}
              classNames="justify-center gap-[8px]"
            />
          </motion.div>
        )}
      </div>
      <div className={cn('relative mt-1.5 mb-[1.375rem]', css.imageBlock)}>
        <picture>
          <source srcSet={img.webp} type="image/webp" />
          <img src={img.png} alt={`${name} box`} />
        </picture>
        <div className="absolute top-[50%] left-[50%] w-[230%] h-[150%] translate-x-[-50%] translate-y-[-50%] z-[-3]">
          <picture>
            <source srcSet={shineWebp} type="image/webp" />
            <img src={shinePng} alt="shine" className="h-full w-full" />
          </picture>
        </div>
        <RadialCircleGradient
          type={gradient}
          className="top-[50%] left-[50%] w-[230%] h-[150%] translate-x-[-50%] translate-y-[-50%] z-1"
        />
      </div>
      <div className="w-full flex justify-center">
        <ChengeButton
          name={name}
          boxSeconds={timer}
          disable={!!timer || adsIsLoading}
          t={t}
          onBuyBox={onBuyBox}
          statusVIP={statusVIP}
          tgem={tgem}
          isLoading={isLoading || adsIsLoading}
          extraText="Buy for"
          className="!w-[170px] h-[48px]"
        />
      </div>
      {!isBuy ? (
        <Close
          onClick={closeModal}
          imageVariant="modal"
          size="lg"
          className="absolute bottom-[calc(var(--tg-safe-area-inset-bottom,0px)+15px)]"
        />
      ) : (
        <div className="!w-auto absolute bottom-[calc(var(--tg-safe-area-inset-bottom,0px)+30px)]">
          <StringMain onClick={closeModal} weight="extrabold">
            {t('Tap to continue')}
          </StringMain>
        </div>
      )}
    </div>
  );
};

export default BuyMarketBox;
